/*!

=========================================================
* Comprehend PT - Email Verification
=========================================================

* The landing page for the link in the verification email. It redeems the
* token on mount and then sends the user to login. Mirrors ResetPassword.js:
* the link carries ?token=<32 chars>&expiry=<unix seconds>.

=========================================================

*/

// reactstrap components
import { Button, Card, CardBody, Col, Spinner } from 'reactstrap';

import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../api/GlobalContext';

const REDIRECT_DELAY_MS = 4000;

const VerifyEmail = () => {
  // Get the API
  const { apiClient } = useContext(GlobalContext);

  // Get the navigation function
  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const expiryTime = searchParams.get('expiry');
  const email = searchParams.get('email');

  // 'verifying' | 'success' | 'error'
  const [status, setStatus] = useState('verifying');
  const [labelText, setLabelText] = useState('Verifying your email...');

  // Verification tokens are single use, so make sure we only redeem once even
  // if this effect is invoked twice (StrictMode, fast refresh, re-renders).
  const hasRedeemed = useRef(false);

  useEffect(() => {
    if (hasRedeemed.current) return undefined;
    hasRedeemed.current = true;

    if (!token || token.length !== 32) {
      setStatus('error');
      setLabelText(
        'This verification link is not valid. Request a new one below.',
      );
      return undefined;
    }

    // The expiry in the URL is a client-side courtesy so we can show a useful
    // message without a round trip. The backend is still the authority.
    if (expiryTime) {
      const expiryDate = new Date(parseInt(expiryTime, 10) * 1000);
      if (new Date() > expiryDate) {
        setStatus('error');
        setLabelText(
          'This verification link has expired. Request a new one below.',
        );
        return undefined;
      }
    }

    let isCurrentRoute = true;

    apiClient
      .confirmEmailVerification(token)
      .then((data) => {
        if (!isCurrentRoute) return;

        if (data && 'error' in data) {
          setStatus('error');
          setLabelText(data['error']);
          return;
        }

        setStatus('success');
        setLabelText(
          'Your email is verified. Taking you to the sign in page...',
        );
        setTimeout(() => {
          navigate('/auth/login');
        }, REDIRECT_DELAY_MS);
      })
      .catch((e) => {
        if (!isCurrentRoute) return;
        setStatus('error');
        setLabelText('Error: ' + e);
      });

    return () => {
      isCurrentRoute = false;
    };
  }, []); // Run once on mount

  const goResend = () => {
    navigate(
      '/auth/verify/sent' + (email ? `?email=${encodeURIComponent(email)}` : ''),
    );
  };

  return (
    <>
      <Col lg="5" md="7">
        <Card className="bg-secondary shadow border-0">
          <CardBody className="px-lg-5 py-lg-5">
            <div className="text-center mb-3">
              {status === 'verifying' && <Spinner color="primary" />}
              {status === 'success' && (
                <i
                  className="ni ni-check-bold"
                  style={{ fontSize: '2.5rem', color: '#2dce89' }}
                />
              )}
              {status === 'error' && (
                <i
                  className="fa-solid fa-triangle-exclamation"
                  style={{ fontSize: '2.25rem', color: '#dc3545' }}
                />
              )}
            </div>

            <div className="text-center text-muted mb-4">
              <small>{labelText}</small>
            </div>

            {status === 'success' && (
              <div className="text-center">
                <Button
                  className="my-2"
                  color="primary"
                  onClick={() => navigate('/auth/login')}
                >
                  Sign in now
                </Button>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <Button className="my-2" color="primary" onClick={goResend}>
                  Send a new link
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

export default VerifyEmail;
